import React, { useState, useEffect } from 'react';
import { supabaseClient } from '../lib/supabase';
import { HOTEL_ID } from '../lib/supabase/configClient';
import { logger } from '../lib/logger';
import { stayService } from '../lib/stay/stayService';
import { X, Info } from 'lucide-react';
import { useDevice } from '../hooks/useDevice';
import { useCookieConsent } from '../hooks/useCookieConsent';

export const TestButtonsPanel: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { os } = useDevice();
  const { resetConsent } = useCookieConsent();
  
  // Determine if we're on a Mac
  const isMac = os === 'MacOS';
  
  // Display the appropriate keyboard shortcut text
  const shortcutText = isMac ? 'Command+Option+T' : 'Ctrl+Alt+T';

  useEffect(() => {
    // Set up keyboard shortcut (Ctrl+Alt+T on Windows/Linux, Command+Option+T on Mac)
    const handleKeyDown = (e: KeyboardEvent) => {
      // For Mac: Command (metaKey) + Option (altKey) + T
      // For others: Ctrl (ctrlKey) + Alt (altKey) + T
      if ((isMac ? e.metaKey : e.ctrlKey) && e.altKey && e.key === 't') {
        setIsVisible(prev => !prev);
        logger.debug('Test buttons panel toggled', {
          category: 'Testing',
          data: { 
            visible: !isVisible,
            platform: os,
            shortcut: shortcutText
          }
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, isMac, os, shortcutText]);

  const handleTestAlert = async () => {
    try {
      // You can force a date here
      const currentDate = new Date().toISOString().split('T')[0]; // Today
      const currentTime = new Date().toTimeString().split(' ')[0].substring(0, 8); // Current time

      // Check if today is checkout day
      const isCheckoutDay = stayService.isCheckoutDay();
      const hasCheckoutAlertBeenShownToday = stayService.hasCheckoutAlertBeenShownToday();

      console.log('Stay dates:', stayService.getStayDates());
      console.log('Is checkout day:', isCheckoutDay);
      console.log('Checkout alert already shown today:', hasCheckoutAlertBeenShownToday);

      console.log('Calling get_active_alerts RPC with params:', {
        p_hotel_id: HOTEL_ID,
        p_date: currentDate,
        p_time: currentTime,
        p_checkout: isCheckoutDay && !hasCheckoutAlertBeenShownToday
      });

      const response = await supabaseClient.rpc('get_active_alerts', {
        p_hotel_id: HOTEL_ID,
        p_date: currentDate,
        p_time: currentTime,
        p_checkout: isCheckoutDay && !hasCheckoutAlertBeenShownToday
      });

      console.log('Raw RPC Response:', {
        data: response.data,
        error: response.error,
        status: response.status,
        statusText: response.statusText,
        count: response.data?.length || 0
      });

      if (response.data && response.data.length > 0) {
        console.log('First alert data:', JSON.stringify(response.data[0], null, 2));
      } else {
        console.log('No alerts found');
      }

      alert(`RPC call completed. Check console for details.
Found ${response.data?.length || 0} alerts.
${response.error ? `Error: ${response.error.message}` : ''}
Is checkout day: ${isCheckoutDay}
Checkout alert already shown today: ${hasCheckoutAlertBeenShownToday}`);

    } catch (error) {
      console.error('Error calling RPC:', error);
      alert(`Error calling RPC: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const handleSetCheckoutDay = () => {
    try {
      // Set today as the checkout day
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      stayService.setStayDates(yesterday, today);
      
      alert(`Stay dates set to simulate checkout day:
Start: ${yesterday.toLocaleDateString()}
End (checkout): ${today.toLocaleDateString()}`);
      
      // Clear the checkout alert shown flag
      localStorage.removeItem('checkout_alert_shown_date');
      
      console.log('Stay dates set to simulate checkout day:', {
        start: yesterday.toISOString(),
        end: today.toISOString()
      });
    } catch (error) {
      console.error('Error setting checkout day:', error);
      alert(`Error setting checkout day: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const handleReset = () => {
    try {
      // Remove first visit flag
      localStorage.removeItem('has_visited');
      
      // Set specific test dates
      const startDate = new Date('2025-04-10T12:00:00');
      const endDate = new Date('2025-04-13T23:59:59');
      
      // Save dates to service
      stayService.setStayDates(startDate, endDate);
      
      // Show confirmation
      alert(`First visit status reset and test dates set:
- Start: ${startDate.toLocaleDateString()}
- End: ${endDate.toLocaleDateString()}

Reload the page to see the PWA installation banner.`);
      
      logger.debug('First visit status reset and test dates set', {
        category: 'PWA',
        data: { 
          timestamp: new Date().toISOString(),
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        }
      });
    } catch (error) {
      logger.error('Error resetting first visit status', {
        category: 'PWA',
        data: { error }
      });
      alert('Error resetting first visit status: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  const handleClearStayDates = () => {
    try {
      // Clear stay dates
      stayService.clearStayDates();
      
      // Show confirmation
      alert('Stay dates cleared. Reload the page to see the behavior on opening.');
      
      logger.debug('Stay dates cleared', {
        category: 'Stay',
        data: { timestamp: new Date().toISOString() }
      });
    } catch (error) {
      logger.error('Error clearing stay dates', {
        category: 'Stay',
        data: { error }
      });
      alert('Error clearing stay dates: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  const handleResetCookies = () => {
    try {
      resetConsent();
      alert('Cookie consent has been reset. The cookie banner will appear on the next page load.');
      
      logger.debug('Cookie consent reset', {
        category: 'RGPD',
        data: { timestamp: new Date().toISOString() }
      });
    } catch (error) {
      logger.error('Error resetting cookie consent', {
        category: 'RGPD',
        data: { error }
      });
      alert('Error resetting cookie consent: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  const handleTestUpdate = async () => {
    const RECORD_ID = 'a8d4ea11-bd95-4883-9147-9e028a752c7c';
    const NEW_VALUE = "j'ai réussi à update la ligne";

    try {
      // 1. Check if record exists and get its data
      const { data: records, error: checkError } = await supabaseClient
        .from('translations_section')
        .select('*')
        .eq('id', RECORD_ID);

      if (checkError) {
        logger.error('Error checking record existence', {
          category: 'Translation',
          data: { error: checkError, recordId: RECORD_ID }
        });
        throw new Error(`Failed to check record: ${checkError.message}`);
      }

      if (!records || records.length === 0) {
        throw new Error('Record not found');
      }

      const existingRecord = records[0];

      logger.debug('Current record state', {
        category: 'Translation',
        data: { 
          recordId: RECORD_ID,
          currentValue: existingRecord.en || null,
          hotelId: existingRecord.hotel_id,
          sectionId: existingRecord.section_id
        }
      });

      // 2. Update while preserving required fields
      const { data: updateRecords, error: updateError } = await supabaseClient
        .from('translations_section')
        .update({
          en: NEW_VALUE,
          hotel_id: existingRecord.hotel_id,
          section_id: existingRecord.section_id,
          updated_at: new Date().toISOString()
        })
        .eq('id', RECORD_ID)
        .select();

      if (updateError) {
        logger.error('Error updating translation', {
          category: 'Translation',
          data: { 
            error: updateError, 
            recordId: RECORD_ID,
            newValue: NEW_VALUE
          }
        });
        throw new Error(`Failed to update record: ${updateError.message}`);
      }

      if (!updateRecords || updateRecords.length === 0) {
        throw new Error('Update returned no data');
      }

      const updateResult = updateRecords[0];

      logger.debug('Update result', {
        category: 'Translation',
        data: {
          recordId: RECORD_ID,
          before: existingRecord.en || null,
          after: updateResult.en,
          expected: NEW_VALUE
        }
      });

      // 3. Final verification
      const { data: verifyRecords, error: verifyError } = await supabaseClient
        .from('translations_section')
        .select('en')
        .eq('id', RECORD_ID);

      if (verifyError) {
        logger.error('Error verifying update', {
          category: 'Translation',
          data: { error: verifyError, recordId: RECORD_ID }
        });
        throw new Error(`Failed to verify update: ${verifyError.message}`);
      }

      if (!verifyRecords || verifyRecords.length === 0) {
        throw new Error('Verification returned no data');
      }

      const verifyResult = verifyRecords[0];

      if (verifyResult.en !== NEW_VALUE) {
        logger.error('Update verification failed', {
          category: 'Translation',
          data: {
            recordId: RECORD_ID,
            expected: NEW_VALUE,
            actual: verifyResult.en
          }
        });
        throw new Error(`Update verification failed: expected "${NEW_VALUE}" but got "${verifyResult.en}"`);
      }

      logger.debug('Update verified successfully', {
        category: 'Translation',
        data: {
          recordId: RECORD_ID,
          finalValue: verifyResult.en
        }
      });

      alert(`Translation updated successfully!\nBefore: ${existingRecord.en || 'null'}\nAfter: ${verifyResult.en}`);

    } catch (error) {
      logger.error('Translation update process failed', {
        category: 'Translation',
        data: { error }
      });
      alert(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gray-800 text-white p-4 z-[999] max-h-[50vh] overflow-auto">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-bold">Test Panel</h2>
          <div className="bg-gray-700 px-2 py-1 rounded text-xs">
            {shortcutText}
          </div>
        </div>
        <button 
          onClick={() => setIsVisible(false)}
          className="p-2 hover:bg-gray-700 rounded-full"
        >
          <X size={20} />
        </button>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <button
          onClick={handleTestAlert}
          className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded"
        >
          Test Alert RPC
        </button>
        
        <button
          onClick={handleSetCheckoutDay}
          className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded"
        >
          Set Checkout Day
        </button>
        
        <button
          onClick={handleReset}
          className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 px-4 rounded"
        >
          Reset First Visit
        </button>
        
        <button
          onClick={handleClearStayDates}
          className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded"
        >
          Clear Stay Dates
        </button>
        
        <button
          onClick={handleResetCookies}
          className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 px-4 rounded"
        >
          Reset Cookies
        </button>
        
        <button
          onClick={handleTestUpdate}
          className="bg-purple-500 hover:bg-purple-600 text-white font-bold py-2 px-4 rounded"
        >
          Test Update
        </button>
      </div>
      
      <div className="mt-4 text-sm">
        <div className="flex items-center gap-2 mb-2">
          <Info size={16} />
          <p>Current state:</p>
        </div>
        <ul className="list-disc pl-5 mt-2 grid grid-cols-1 md:grid-cols-2 gap-1">
          <li>Has visited: {localStorage.getItem('has_visited') ? 'Yes' : 'No'}</li>
          <li>Has stay dates: {stayService.hasStayDates() ? 'Yes' : 'No'}</li>
          <li>Is checkout day: {stayService.isCheckoutDay() ? 'Yes' : 'No'}</li>
          <li>Checkout alert shown today: {stayService.hasCheckoutAlertBeenShownToday() ? 'Yes' : 'No'}</li>
          <li>Dates expired: {stayService.areDatesExpired() ? 'Yes' : 'No'}</li>
          <li>Cookie consent: {localStorage.getItem('cookie_consent') ? 'Set' : 'Not set'}</li>
        </ul>
      </div>
    </div>
  );
};